import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Download, ChevronUp, ChevronDown } from 'lucide-react';
import screenService from '@/utils/services/ScreenService.js';
import { useAuth } from '@/contexts/AuthContext';
import { hasActiveSubscription } from '@/utils/subscriptionUtils';

const EFFECTS = [
  { label: 'None', value: 'none' },
  { label: 'Fade In', value: 'fadein' },
  { label: 'Zoom In', value: 'zoomin' },
  { label: 'Left to Right', value: 'ltr' },
  { label: 'Right to Left', value: 'rtl' },
  { label: 'Top to Bottom', value: 'ttb' },
  { label: 'Bottom to Top', value: 'btt' },
];

const CreateSlideshowModal = ({ onClose, onCreate, currentUserData }) => {
  const [screenName, setScreenName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [slides, setSlides] = useState([]); // [{file, animation, duration, previewUrl}]
  const [angle, setAngle] = useState(0);
  const fileInputRef = useRef();
  const { currentUser } = useAuth();
  
  // Check if user has active subscription (not a free user)
  const isFreeUser = !hasActiveSubscription(currentUserData);
  const MAX_FREE_SLIDES = 5;

  // Load pre-filled data from sessionStorage
  useEffect(() => {
    const prefillScreenName = sessionStorage.getItem('prefillScreenName')
    
    if (prefillScreenName) {
      setScreenName(prefillScreenName)
      sessionStorage.removeItem('prefillScreenName')
    }
  }, [])

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Get aspect ratio based on angle
  const getImageAspectRatio = (angle) => {
    switch(angle) {
      case 0:
      case 180:
        return "16/9";
      case 90:
      case 270:
        return "9/16";
      default:
        return "16/9";
    }
  };

  // Rotate left (counter-clockwise)
  const rotateLeft = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle + 90) % 360;
      return newAngle;
    });
  };

  // Rotate right (clockwise)
  const rotateRight = () => {
    setAngle(prevAngle => {
      const newAngle = (prevAngle - 90 + 360) % 360;
      return newAngle;
    });
  };

  // Open file selector
  const handleSelectFiles = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if free user is trying to exceed limit
    if (isFreeUser && (slides.length + files.length) > MAX_FREE_SLIDES) {
      setError(`Free users can upload a maximum of ${MAX_FREE_SLIDES} slides. Please upgrade your subscription to add more slides.`);
      return;
    }
    
    // Validate each file
    for (const file of files) {
      // Validate file type - only images allowed
      if (!file.type.startsWith('image/')) {
        setError('Please select valid image files only.');
        return;
      }
      
      // Validate file size (max 10MB for images)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB.');
        return;
      }
    }
    
    setError('');
    const newSlides = files.map(file => ({
      file,
      animation: 'fadein',
      duration: 10,
      previewUrl: URL.createObjectURL(file),
    }));
    setSlides(prev => [...prev, ...newSlides]);
  };

  const handleAddSlide = () => {};
  const handleRandomize = () => {
    setSlides(prev => prev.map(slide => ({
      ...slide,
      animation: EFFECTS[Math.floor(Math.random() * EFFECTS.length)].value
    })));
  };
  const handleCreate = async () => {
    if (!screenName.trim()) {
      setError('Please enter a screen name.');
      console.log('No screen name provided');
      return;
    }
    if (slides.length === 0) {
      setError('Please add at least one slide.');
      console.log('No slides provided');
      return;
    }
    if (isFreeUser && slides.length > MAX_FREE_SLIDES) {
      setError(`Free users can upload a maximum of ${MAX_FREE_SLIDES} slides. Please upgrade your subscription to add more slides.`);
      console.log('Free user exceeding slide limit');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Prepare screenData
      const screenData = {
        name: screenName.trim(),
        angle: angle,
        slides: true, // Mark as slideshow
        slidesData: slides.map(slide => ({
          animation: slide.animation,
          duration: slide.duration,
        })),
      };
      console.log('Prepared screenData:', screenData);
      // Convert files to base64
      const rawImages = await Promise.all(slides.map(slide => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(slide.file);
        });
      }));
      console.log('Converted files to base64:', rawImages);
      
      // Use ScreenService.addSlides with progress callback
      await screenService.addSlides(
        screenData, 
        rawImages, 
        currentUser?.uid,
        (progress) => {
          console.log('Progress:', progress);
          // You can add a progress state here if needed
        }
      );
      
      console.log('Slideshow created successfully');
      setLoading(false);
      if (onCreate) onCreate();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create slideshow.');
      setLoading(false);
      console.error('Error in handleCreate:', err);
    }
  };

  const handleDeleteSlide = (idx) => {
    setSlides(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEffectChange = (idx, animation) => {
    setSlides(prev => prev.map((slide, i) => i === idx ? { ...slide, animation } : slide));
  };

  const handleSecondsChange = (idx, duration) => {
    setSlides(prev => prev.map((slide, i) => i === idx ? { ...slide, duration: Number(duration) } : slide));
  };

  const handleMoveUp = (idx) => {
    if (idx > 0) {
      setSlides(prev => {
        const newSlides = [...prev];
        [newSlides[idx - 1], newSlides[idx]] = [newSlides[idx], newSlides[idx - 1]];
        return newSlides;
      });
    }
  };

  const handleMoveDown = (idx) => {
    setSlides(prev => {
      if (idx < prev.length - 1) {
        const newSlides = [...prev];
        [newSlides[idx], newSlides[idx + 1]] = [newSlides[idx + 1], newSlides[idx]];
        return newSlides;
      }
      return prev;
    });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-2xl text-gray-900">Create Slideshow</span>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors" aria-label="Close">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="px-12 py-8 bg-gray-50 flex-1 overflow-y-auto">
          <label className="block mb-3 font-medium text-gray-700 text-lg">Screen Name</label>
          <input
            className="w-full border border-gray-200 rounded-lg px-6 py-4 mb-2 text-xl focus:outline-none focus:ring-2 focus:ring-yellow-400"
            placeholder="Enter the screen name"
            value={screenName}
            onChange={e => setScreenName(e.target.value)}
            disabled={loading}
            maxLength={64}
          />
          <p className="text-sm text-gray-500 mb-6">Give your slideshow a descriptive name to easily identify it later</p>

          <div className="flex gap-4 mb-4">
            <button
              className="flex-1 px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-lg font-semibold flex items-center justify-center gap-2"
              onClick={handleSelectFiles}
              type="button"
              disabled={loading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" /></svg>
              Select Images
            </button>
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              className="hidden"
              onChange={handleFilesChange}
              disabled={loading}
            />
            <button
              className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-lg font-semibold flex items-center justify-center gap-2"
              onClick={handleRandomize}
              type="button"
              disabled={loading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A9 9 0 116.582 9M19.418 7V4h-3" /></svg>
              Randomize Effects
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">💡 For best results, use images with 1920x1080 resolution (16:9 aspect ratio)</p>
          {/* Rotation Controls */}
          {slides.length > 0 && (
            <div className="mb-6">
              <label className="block mb-3 font-medium text-gray-700 text-lg">TV Orientation</label>
              <div className="flex justify-center">
                <div className="btn-group">
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-l-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateLeft}
                    disabled={loading}
                    title="Rotate Left"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors text-base font-medium"
                    onClick={rotateRight}
                    disabled={loading}
                    title="Rotate Right"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-center">🔄 Use the rotation arrows to adjust the TV orientation for your display setup</p>
            </div>
          )}
          {/* Slides List */}
          {slides.length > 0 && (
            <div className={slides.length > 2 ? "max-h-[40vh] overflow-y-auto space-y-6 mb-6" : "space-y-6 mb-6"}>
              {slides.map((slide, idx) => (
                <div key={idx} className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-1 w-full md:w-auto flex flex-col gap-3">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-base text-gray-800">Effect</span>
                      <select
                        className="ml-2 px-3 py-2 rounded border border-gray-300 bg-white text-gray-800 text-base font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={slide.animation}
                        onChange={e => handleEffectChange(idx, e.target.value)}
                      >
                        {EFFECTS.map(effect => (
                          <option key={effect.value} value={effect.value}>{effect.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-base text-gray-800">Show duration</span>
                      <input
                        type="number"
                        min={1}
                        className="w-20 px-3 py-2 rounded border border-gray-300 text-base bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={slide.duration}
                        onChange={e => handleSecondsChange(idx, e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      {/* Move Up Button */}
                      <button 
                        className="p-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                        title="Move Up" 
                        onClick={() => handleMoveUp(idx)}
                        disabled={idx === 0}
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                      
                      {/* Move Down Button */}
                      <button 
                        className="p-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                        title="Move Down" 
                        onClick={() => handleMoveDown(idx)}
                        disabled={idx === slides.length - 1}
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                      
                      <a
                        href={slide.previewUrl}
                        download={slide.file?.name || 'slide'}
                        className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center justify-center"
                        title="Download"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        <Download className="w-5 h-5" />
                      </a>
                      <button className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors" title="Delete" onClick={() => handleDeleteSlide(idx)}>
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {(angle === 0 || angle === 180 || !angle) ? (
                      <>
                        {/* Landscape TV Frame (0°, 180°, or no angle) */}
                        <div 
                          className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                          style={{
                            borderBottomWidth: (angle === 0 || !angle) ? '20px' : '2px',
                            borderRightWidth: '2px',
                            borderTopWidth: angle === 180 ? '20px' : '2px',
                            borderLeftWidth: '2px',
                            width: '192px',
                            height: '128px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                            borderColor: '#1f2937',
                            borderStyle: 'solid'
                          }}
                        >
                          <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                            <img 
                              src={slide.previewUrl} 
                              alt="Slide preview" 
                              className="w-full h-full object-contain rounded-lg"
                              style={{
                                transition: 'transform 0.3s ease',
                                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                              }}
                            />
                          </div>
                        </div>
                        {/* TV Power Button - Separate Element */}
                        {(angle === 0 || angle === 180 || !angle) && (
                          <div 
                            className="absolute z-30"
                            style={{
                              bottom: (angle === 0 || !angle) ? '4px' : 'auto',
                              top: angle === 180 ? '4px' : 'auto',
                              left: '50%',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <div className="w-4 h-4 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full border border-gray-400 shadow-md flex items-center justify-center">
                              <div className="w-1 h-1 bg-red-400 rounded-full shadow-inner"></div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Portrait TV Frame (90° and 270°) */}
                        <div 
                          className="relative border-2 border-black inline-block p-1 m-1 overflow-hidden transition-transform duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 shadow-2xl rounded-lg"
                          style={{
                            borderBottomWidth: '2px',
                            borderRightWidth: angle === 270 ? '20px' : '2px',
                            borderTopWidth: '2px',
                            borderLeftWidth: angle === 90 ? '20px' : '2px',
                            width: '128px',
                            height: '192px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(255, 193, 7, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                            background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 50%, #fde68a 100%)',
                            borderColor: '#1f2937',
                            borderStyle: 'solid'
                          }}
                        >
                          <div className="overflow-hidden flex items-center justify-center w-full h-full rounded-md bg-gradient-to-br from-gray-900 to-gray-800 p-1 relative">
                            <img 
                              src={slide.previewUrl} 
                              alt="Slide preview" 
                              className="w-full h-full object-contain rounded-lg"
                              style={{
                                transition: 'transform 0.3s ease',
                                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
                              }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {/* Divider between slides */}
              {slides.length > 1 && slides.map((_, idx) => idx < slides.length - 1 && (
                <div key={'divider-' + idx} className="border-t border-gray-200 mx-2" />
              ))}
            </div>
          )}
          {error && <div className="text-red-500 mb-3 text-base">{error}</div>}
        </div>
        {/* Actions */}
        <div className="flex justify-end gap-3 px-12 py-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
          <button
            className="px-5 py-3 rounded-lg text-lg text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onClose}
            disabled={loading}
          >Close</button>
          <button
            className="px-5 py-3 rounded-lg bg-yellow-500 text-white text-lg font-semibold hover:bg-yellow-600 transition-colors disabled:opacity-60"
            onClick={handleCreate}
            disabled={loading}
          >{loading ? 'Creating...' : 'Create Slideshow'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateSlideshowModal; 